'use client'

import { RefObject, useCallback, useMemo } from 'react'
import { modify } from '@/app/actions/reservation-modification'
import type { Locale } from '@/root/shared-types'
import type { Price } from '@/root/shared-types/dto/SailPricing'
import type { Key } from '@/root/shared-hooks/useGetTranslations/useGetTranslations'
import { throwResidentDiscountError } from '@/root/shared-helpers/resident'
import { getResidentPriceCategory } from '@/app/actions/resident'
import type { ReservationModification, ReservationWithModification } from '../types'
import { MODIFY_ERROR_MESSAGE_KEYS } from '../consts'
import { buildAddTicketsModification, handleStaleServerAction } from '../utils'

interface UseAddResidentFlowParams {
  currentReservation: ReservationWithModification
  reservationToken: string
  locale: Locale
  sailPackageSeqN: number
  sailPackageCode: string
  sailRefId: number | undefined
  sailRefIds: number[]
  isPendingRef: RefObject<boolean>
  setIsPending: (pending: boolean) => void
  getTranslation: (key: Key) => string
  onReservationUpdated: (updated: ReservationWithModification) => void
  onError?: (message: string, code?: string) => void
  onErrorClear?: () => void
}

export interface ResidentOverrideToggleParams {
  personalId: string
  /** Guest the ticket belongs to now — its item is the one being replaced. */
  guestSeqN: number
  /** Category to move to when the option is switched on. */
  overrideCode: string
  checked: boolean
}

export interface ResidentOverrideToggleResult {
  priceCategory: string
  guestSeqN: number
}

interface UseAddResidentFlowReturn {
  route: string | undefined
  departureDate: string | undefined
  handleResidentIdVerified: (
    price: Price,
    slotIndex: number,
    personalId: string,
    verifiedPriceCategory: string
  ) => Promise<number | null>
  toggleResidentOverride: (params: ResidentOverrideToggleParams) => Promise<ResidentOverrideToggleResult | null>
}

export const useAddResidentFlow = ({
  currentReservation,
  reservationToken,
  locale,
  sailPackageSeqN,
  sailPackageCode,
  sailRefId,
  sailRefIds,
  isPendingRef,
  setIsPending,
  getTranslation,
  onReservationUpdated,
  onError,
  onErrorClear,
}: UseAddResidentFlowParams): UseAddResidentFlowReturn => {
  const currentSailRef = useMemo(
    () =>
      currentReservation.sailPackages
        ?.find((sp) => sp.seqN === sailPackageSeqN)
        ?.sailRefs?.find((sr) => sr.sailRefId === sailRefId),
    [currentReservation.sailPackages, sailPackageSeqN, sailRefId]
  )

  const applyResident = useCallback(
    async (personalId: string, priceCategory: string, replacedGuestSeqN?: number): Promise<number | null> => {
      if (isPendingRef.current) return null
      if (!sailPackageCode || sailRefIds.length === 0) return null
      onErrorClear?.()

      const maxGuestSeqN = (currentReservation.guests ?? []).reduce((max, g) => Math.max(max, g.seqN), 0)
      const newSeqN = maxGuestSeqN + 1

      const modifications: ReservationModification[] = [
        {
          __type: 'addItems',
          ticketsRequests: buildAddTicketsModification({
            sailPackage: sailPackageCode,
            sailRefIds,
            priceCategory,
            quantity: 1,
            startSeqN: newSeqN,
            personalIdentificationNumber: personalId,
          }),
        },
      ]

      if (replacedGuestSeqN !== undefined) {
        modifications.push({
          __type: 'deleteItems',
          ticketsRequests: [{ sailPackageSeqN, seqN: replacedGuestSeqN }],
        })
      }

      isPendingRef.current = true
      setIsPending(true)

      try {
        let modifyResult: Awaited<ReturnType<typeof modify>>
        try {
          modifyResult = await modify(reservationToken, modifications, locale)
        } catch (err) {
          if (
            handleStaleServerAction({
              error: err,
              getTranslation,
              onError: (message) => onError?.(message),
            })
          ) {
            return null
          }
          throw err
        }

        if (!modifyResult.success) {
          const apiCode = modifyResult.error.code
          throwResidentDiscountError(getTranslation, apiCode)

          const messageKey = (apiCode && MODIFY_ERROR_MESSAGE_KEYS[apiCode]) || 'commonNotificationErrorDesc'
          const errorMessage = getTranslation(messageKey)
          onError?.(errorMessage, apiCode)
          throw new Error(errorMessage)
        }

        onReservationUpdated(modifyResult.data)
        return newSeqN
      } finally {
        isPendingRef.current = false
        setIsPending(false)
      }
    },
    [
      currentReservation.guests,
      reservationToken,
      sailPackageCode,
      sailPackageSeqN,
      sailRefIds,
      locale,
      isPendingRef,
      setIsPending,
      getTranslation,
      onReservationUpdated,
      onError,
      onErrorClear,
    ]
  )

  const handleResidentIdVerified = useCallback(
    (_price: Price, _slotIndex: number, personalId: string, verifiedPriceCategory: string): Promise<number | null> =>
      applyResident(personalId, verifiedPriceCategory),
    [applyResident]
  )

  /**
   * Moves an existing resident ticket onto the override category and back.
   */
  const toggleResidentOverride = useCallback(
    async ({
      personalId,
      guestSeqN,
      overrideCode,
      checked,
    }: ResidentOverrideToggleParams): Promise<ResidentOverrideToggleResult | null> => {
      let priceCategory = overrideCode

      if (!checked) {
        const route = currentSailRef?.route
        const departureDate = currentSailRef?.departureDate
        if (!route || !departureDate) throw new Error('No sail to price the resident ticket on')

        const lookup = await getResidentPriceCategory({ route, residentId: personalId, departureDate })

        if ('error' in lookup) {
          throwResidentDiscountError(getTranslation, lookup.error.code)
          const errorMessage = getTranslation('commonNotificationErrorDesc')
          onError?.(errorMessage, lookup.error.code)
          throw new Error(errorMessage)
        }

        priceCategory = lookup.data.priceCategory
      }

      const newGuestSeqN = await applyResident(personalId, priceCategory, guestSeqN)
      if (newGuestSeqN === null) return null
      return { priceCategory, guestSeqN: newGuestSeqN }
    },
    [applyResident, currentSailRef?.route, currentSailRef?.departureDate, getTranslation, onError]
  )

  return {
    route: currentSailRef?.route,
    departureDate: currentSailRef?.departureDate,
    handleResidentIdVerified,
    toggleResidentOverride,
  }
}
